import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  type AutocompleteRenderOptionState,
  Box,
  CircularProgress,
  createFilterOptions,
  type FilterOptionsState,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { DeepKeys } from '@tanstack/react-table';
import { Field, useFormikContext } from 'formik';
import { camelCase, get, has } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

type OptionType<T> = { _id: string | number } & T;

// Internal sentinel option used when no results are found
type SentinelOption = { _id: string; label?: string; __isAddNew?: boolean };

// The effective option type the Autocomplete will receive
type AnyOption<T> = OptionType<T> | SentinelOption;

// Supported MUI Autocomplete sizes
type AutocompleteSize = 'small' | 'medium';

// Type guard to detect sentinel (non-data) options
function isSentinelOption(option: unknown): option is SentinelOption {
  return (
    !!option &&
    typeof option === 'object' &&
    ('__isAddNew' in (option as Record<string, unknown>) ||
      (option as { _id?: unknown })._id === '__add__')
  );
}

export type InputTypeaheadActions = Record<
  string,
  (payload: { inputValue: string }) => void
>;

export interface InputTypeaheadProps<T> {
  label: string;
  options: AnyOption<T>[];
  idKey?: string;
  labelKey?: DeepKeys<T>;
  disabled?: boolean;
  required?: boolean;
  showLabel?: boolean;
  nameOverride?: string;
  multiple?: boolean;
  allowNew?: boolean;
  minSelection?: number;
  maxSelection?: number;
  addNoResultsOptionLabel?: string;
  actions?: InputTypeaheadActions;
  noResultsActionKey?: string;
  size?: AutocompleteSize;
  onInputChange?: (value: string) => void;
  isLoading?: boolean;
  hint?: string;
  getOptionDescription?: (option: OptionType<T>) => React.ReactNode;
}

export function InputTypeahead<T extends string | Record<string, unknown>>({
  label,
  disabled = false,
  idKey = 'id',
  labelKey = undefined,
  required = false,
  options,
  nameOverride = undefined,
  multiple = false,
  showLabel = true,
  allowNew = false,
  minSelection = undefined,
  maxSelection = undefined,
  addNoResultsOptionLabel = undefined,
  actions = undefined,
  noResultsActionKey = 'addNew',
  size = 'medium',
  onInputChange = undefined,
  isLoading = false,
  hint = undefined,
  getOptionDescription = undefined,
}: InputTypeaheadProps<T>) {
  const { values, errors, setFieldValue, setFieldTouched } =
    useFormikContext<Record<string, unknown>>();

  const [inputValue, setInputValue] = useState('');

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const validate = useCallback(
    (value: unknown) => {
      if (multiple && Array.isArray(value)) {
        if (required && (!value || value.length === 0)) {
          return `${label} is required.`;
        }
        if (minSelection && value.length < minSelection) {
          return `${label} must have at least ${minSelection} selection${minSelection > 1 ? 's' : ''}.`;
        }
        if (maxSelection && value.length > maxSelection) {
          return `${label} can have at most ${maxSelection} selection${maxSelection > 1 ? 's' : ''}.`;
        }
      } else if (
        required &&
        (!value || value === null || value === undefined)
      ) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required, multiple, minSelection, maxSelection],
  );

  const isInvalid = useMemo(
    () => has(errors, name) && get(errors, name) !== '',
    [errors, name],
  );

  const currentValue = useMemo(() => {
    const fieldValue = get(values, name);
    if (multiple) {
      return Array.isArray(fieldValue) ? fieldValue : [];
    }
    return fieldValue || null;
  }, [values, name, multiple]);

  const getOptionLabel = useCallback(
    (option: AnyOption<T> | string) => {
      if (typeof option === 'string') return option;
      if (option && 'label' in option && option.label) {
        return option.label;
      }
      if (labelKey) {
        return get(option as OptionType<T>, labelKey as string) || '';
      }
      return (option as OptionType<T>)._id?.toString() || '';
    },
    [labelKey],
  );

  const getOptionValue = useCallback(
    (option: OptionType<T>) => get(option, idKey) || option._id,
    [idKey],
  );

  const isOptionEqualToValue = useCallback(
    (option: AnyOption<T>, value: AnyOption<T>) => {
      if (isSentinelOption(option) || isSentinelOption(value)) return false;
      return (
        getOptionValue(option as OptionType<T>) ===
        getOptionValue(value as OptionType<T>)
      );
    },
    [getOptionValue],
  );

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent,
      newValue: (string | OptionType<T>)[] | string | OptionType<T> | null,
    ) => {
      // Intercept sentinel option in single-select mode
      if (!multiple && newValue && typeof newValue === 'object') {
        const maybeObj = newValue as any;
        if (maybeObj.__isAddNew || maybeObj._id === '__add__') {
          const fn = actions?.[noResultsActionKey];
          if (fn) fn({ inputValue });
          return;
        }
      }

      let processedValue: unknown;

      if (multiple) {
        processedValue = Array.isArray(newValue)
          ? newValue.map((item) =>
              typeof item === 'string' ? item : getOptionValue(item),
            )
          : [];
      } else {
        processedValue =
          newValue === null || typeof newValue === 'string'
            ? newValue
            : getOptionValue(newValue as OptionType<T>);
      }

      setFieldValue(name, processedValue);
    },
    [
      multiple,
      setFieldValue,
      name,
      actions,
      noResultsActionKey,
      inputValue,
      getOptionValue,
    ],
  );

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  const selectedOptions = useMemo(() => {
    if (!currentValue) return multiple ? [] : null;

    if (multiple) {
      return Array.isArray(currentValue)
        ? options.filter(
            (option) =>
              !isSentinelOption(option) &&
              currentValue.includes(getOptionValue(option as OptionType<T>)),
          )
        : [];
    }

    // Find the option in the current options list
    const foundOption = options.find(
      (option) =>
        !isSentinelOption(option) &&
        getOptionValue(option as OptionType<T>) === currentValue,
    );

    return foundOption || null;
  }, [currentValue, multiple, options, getOptionValue]);

  return (
    <FormControl fullWidth error={isInvalid}>
      <Field
        as={Autocomplete}
        id={name}
        name={name}
        validate={validate}
        disabled={disabled}
        multiple={multiple}
        freeSolo={allowNew}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        onBlur={handleBlur}
        getOptionLabel={getOptionLabel}
        getOptionKey={(option: AnyOption<T>) => {
          if (typeof option === 'string') return option;
          return option._id?.toString() || '';
        }}
        isOptionEqualToValue={isOptionEqualToValue}
        onInputChange={(
          _e: React.SyntheticEvent,
          val: string,
          reason: string,
        ) => {
          setInputValue(val);
          // Only notify parent of input changes, not resets after selection
          if (reason === 'input' || reason === 'clear') {
            onInputChange?.(val);
          }
        }}
        size={size}
        loading={isLoading}
        loadingText="Searching..."
        filterOptions={(
          opts: AnyOption<T>[],
          params: FilterOptionsState<AnyOption<T>>,
        ) => {
          // If we're using external search with loading indicator,
          // don't apply default filtering - options are pre-filtered
          if (
            onInputChange &&
            params.inputValue &&
            params.inputValue.length >= 2
          ) {
            return opts;
          }

          const defaultFilter = createFilterOptions<AnyOption<T>>();
          const filtered = defaultFilter(opts, params);

          if (
            addNoResultsOptionLabel &&
            params.inputValue &&
            params.inputValue.trim() !== '' &&
            filtered.length === 0
          ) {
            const sentinel: SentinelOption = {
              _id: '__add__',
              label: addNoResultsOptionLabel,
              __isAddNew: true,
            };
            // Return the single sentinel option to render "Add New …"
            return [sentinel as AnyOption<T>];
          }

          // Otherwise, return the filtered results
          return filtered as AnyOption<T>[];
        }}
        renderOption={
          getOptionDescription
            ? (
                props: React.HTMLAttributes<HTMLLIElement>,
                option: AnyOption<T>,
                _state: AutocompleteRenderOptionState,
              ) => {
                // Skip description rendering for sentinel options
                if (isSentinelOption(option)) {
                  return (
                    <li {...props}>
                      <Box sx={{ width: '100%' }}>{getOptionLabel(option)}</Box>
                    </li>
                  );
                }

                const description = getOptionDescription(
                  option as OptionType<T>,
                );
                const optionLabel = getOptionLabel(option);

                return (
                  <li {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1">{optionLabel}</Typography>
                      {description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }
            : undefined
        }
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={showLabel ? label : undefined}
            placeholder={label}
            required={required}
            error={isInvalid}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              ...(isLoading && {
                endAdornment: (
                  <>
                    <CircularProgress color="inherit" size={20} />
                    {params.InputProps.endAdornment}
                  </>
                ),
              }),
            }}
          />
        )}
      />
      {(isInvalid || hint) && (
        <FormHelperText error={isInvalid}>
          {isInvalid ? String(get(errors, name)) : hint}
        </FormHelperText>
      )}
    </FormControl>
  );
}
